import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Combines class names with Tailwind CSS conflict resolution.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Formats a numeric amount to Philippine Pesos with monospace tabular precision.
 * Example: 1382400 -> "₱1,382,400.00"
 */
export function formatCurrency(amount: number | null | undefined): string {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '₱0.00';
  }
  const formatted = Math.abs(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return amount < 0 ? `-₱${formatted}` : `₱${formatted}`;
}

/**
 * Formats a weight value in kilograms.
 * Example: 10240 -> "10,240.0 kg"
 */
export function formatWeight(weightKg: number | null | undefined, decimals = 1): string {
  if (weightKg === null || weightKg === undefined || isNaN(weightKg)) {
    return '0.0 kg';
  }
  return `${weightKg.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })} kg`;
}

/**
 * Formats animal headcount with singular/plural suffix.
 * Example: 105 -> "105 head"
 */
export function formatHead(headCount: number | null | undefined): string {
  if (headCount === null || headCount === undefined || isNaN(headCount)) {
    return '0 head';
  }
  return `${Math.round(headCount).toLocaleString('en-US')} head`;
}

/**
 * Formats bag count.
 * Example: 840 -> "840 bags"
 */
export function formatBags(bags: number | null | undefined): string {
  if (bags === null || bags === undefined || isNaN(bags)) {
    return '0 bags';
  }
  return `${bags.toLocaleString('en-US')} bags`;
}

/**
 * Formats ratio or percentage.
 * Example: 2.18 -> "2.18" or 0.0095 -> "0.95%"
 */
export function formatPercent(rate: number | null | undefined, isDecimalRate = false): string {
  if (rate === null || rate === undefined || isNaN(rate)) {
    return '0.0%';
  }
  const pct = isDecimalRate ? rate * 100 : rate;
  return `${pct.toFixed(2)}%`;
}

/**
 * Formats date string into readable Philippine / standard format.
 * Example: "2026-09-01" -> "Sep 01, 2026"
 */
export function formatDate(dateString: string | Date | null | undefined): string {
  if (!dateString) return '—';
  try {
    const d = typeof dateString === 'string' ? new Date(dateString) : dateString;
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  } catch {
    return '—';
  }
}

/**
 * Formats timestamp with date and time.
 * Example: "2026-09-01T08:30:00Z" -> "Sep 01, 2026 8:30 AM"
 */
export function formatDateTime(dateString: string | Date | null | undefined): string {
  if (!dateString) return '—';
  try {
    const d = typeof dateString === 'string' ? new Date(dateString) : dateString;
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '—';
  }
}

/**
 * Formats a generic numeric value with comma separation.
 * Example: 1040 -> "1,040"
 */
export function formatNumber(num: number | null | undefined, decimals = 0): string {
  if (num === null || num === undefined || isNaN(num)) {
    return '0';
  }
  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Formats file size in bytes to human-readable string (KB, MB).
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}


